import type { CSSProperties } from "react";

import type { PresentedColumn } from "../lib/tablePresentation";

export interface PresentedTableProps {
  columns: PresentedColumn[];
  rows: (string | null)[][];
  /** Columns dropped by the column cap (renders a "+N more" affordance). */
  hiddenByCap?: number;
}

// MER-377: header row reads as an operator report, not a raw dump -- bold
// label weight plus the brand green-to-teal bar (same gradient DemoBanner
// and the tab-active wash already use) under the header row instead of the
// generic 1px border-subtle line every other panel edge uses. Sticky so the
// bar and the labels scroll with the header on a tall table.
const headerStyle: CSSProperties = {
  padding: "8px 12px",
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--text-muted)",
  borderBottom: "2px solid var(--brand-teal)",
  position: "sticky",
  top: 0,
  background: "var(--bg-card)",
  whiteSpace: "nowrap",
};

const cellStyle: CSSProperties = {
  padding: "10px 12px",
  fontSize: 14,
  borderBottom: "1px solid var(--border-subtle)",
  whiteSpace: "nowrap",
};

// MER-377: subtle alternating row background so a wide row of numbers can be
// read across without losing your place. Kept faint (the app background
// tint, not a new color) so it reads as texture, not a stripe.
const stripedRowStyle: CSSProperties = { background: "var(--bg-app)" };

/**
 * MER-438: renders a tabular widget from its presented (allow-listed, humanized,
 * formatted) columns + rows. Sticky header, right-aligned numerics, brand-card
 * density, and a bounded scroll container so a wide table never collides with
 * its side-by-side neighbor. Replaces the raw Grid.js dump for manifest tables;
 * the drill-down modal keeps Grid.js for large, searchable result sets.
 *
 * MER-377: general styling polish so a table reads like an operator report --
 * bold header labels under the brand green-to-teal bar, and a faint alternating
 * row tint so a wide row of numbers can be read across without losing your
 * place. Formatting only: presentTable (tablePresentation.ts) still owns every
 * value and header string this component renders.
 */
export default function PresentedTable({ columns, rows, hiddenByCap = 0 }: PresentedTableProps) {
  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      <div style={{ maxHeight: 420, overflow: "auto" }}>
        {/* MER-438 addendum: minWidth (not width) so a many-column table grows
            past the container and the wrapper scrolls horizontally, instead of
            squishing columns until the nowrap text overlaps. */}
        <table style={{ minWidth: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} style={{ ...headerStyle, textAlign: c.align }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} style={ri % 2 === 1 ? stripedRowStyle : undefined}>
                {row.map((cell, ci) => {
                  const align = columns[ci]?.align ?? "left";
                  return (
                    <td
                      key={ci}
                      style={{
                        ...cellStyle,
                        textAlign: align,
                        fontWeight: align === "right" ? 500 : undefined,
                      }}
                    >
                      {cell ?? "-"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hiddenByCap > 0 ? (
        <div style={{ padding: "6px 12px", fontSize: 11, color: "var(--text-muted)" }}>
          +{hiddenByCap} more column{hiddenByCap === 1 ? "" : "s"} hidden
        </div>
      ) : null}
    </div>
  );
}
